/** ALFX Web Dashboard Map Manager 
 *    - Initialized when map is created
 *    - Handles annotations and overlays
*/
import { mappableEntitiesInSearchOrder, allCategories, itemsForCategories, isService, allInfrastructures, allPipeSizes, 
    coordinateOfMappable, coordinatesOfPipe, twoLineAddressOfMappable, connectionCoordinatesOfPipe, doesItemMatch, 
    dateTextforTimeMS } from "./wpn2Model.js";

const AnnotOffset = new DOMPoint(0, -16);

let map = null;
let mapPrefs = null;
let sysData = null;
let currentAnnotations = new Map();
let currentOverlays = {
    'Pipe': new Map(),
    'PipeConnection': new Map(),
    'Sensor': new Map(),
    'SoundPath': new Map()
};
const statusColorMap = {
    Normal: "green", Green: "green",
    Monitor: "yellow", Yellow: "yellow",
    Alert: "red", Red: "red"
};
let pipeConnectionStyle = null;
let heatMapStyles = {
    "green": null,
    "yellow": null,
    "red": null,
    "gray": null,
}

/**
 * Initialize the annotation manager with a map instance.
 */
export function initMapManager(mapPreferences, systemData) {
    // export function initMapManager(/*mapInstance, */mapPreferences, systemData) {
    if (map && map !== mapPreferences.mapInstance) {
        // if (map && map !== mapInstance) {
        console.warn('annotationManager already initialized with a different map. Clearing first.');
        clearAnnotations();
    }
    map = mapPreferences.mapInstance;  // mapInstance;
    mapPrefs = mapPreferences;
    sysData = systemData;
    pipeConnectionStyle = new mapkit.Style({ strokeColor: "silver", strokeOpacity: 1, 
        fillColor: "silver", fillOpacity: 1, lineWidth: 1});
    ["green", "yellow", "red", "gray"].forEach(x => {
        heatMapStyles[x] = new mapkit.Style({ strokeColor: x, strokeOpacity: 0.25, fillColor: x, fillOpacity: 0.25, lineWidth: 1 });
    })
}

/**
 * Clean up all annotations and reset state.
 */
export function resetMapManager() {
    if (!map) return;
    for (const annotation of currentAnnotations.values()) {
        map.removeAnnotation(annotation);
    }
    currentAnnotations.clear();
    for (const overlayType in currentOverlays) {
        const workingOverlays = currentOverlays[overlayType] ?? new Map();
        for (const [uuid, overlays] of workingOverlays) {
            map.removeOverlays(overlays);
        }
    }
    currentOverlays = { 'Pipe': new Map(), 'PipeConnection': new Map(), 'Sensor': new Map(), 'SoundPath': new Map() };
    map = null;
}

/**
 * Update annotations based on passed data items, filtered to reconcile with map preferences.
 * @param {Array} data - Array of data items, like Sensor or FIxedAsset
 */
function updateAnnotations(items = []) {
    if (!map) return;
    const itemIds = new Set();
    for (const x of items) {
        itemIds.add(x.uuid);
        if (!currentAnnotations.has(x.uuid)) {
            const annotation = makeAnnotationWithCallout(x);
            map.addAnnotation(annotation);
            currentAnnotations.set(x.uuid, annotation);
        }
    }
    // Remove annotations that are no longer needed
    for (const [uuid, annotation] of currentAnnotations.entries()) {
        if (!itemIds.has(uuid)) {
            if (!map.annotations.includes(annotation)) {
                console.warn("Annotation not found on map, skipping removal:", uuid);
            } else {
                map.removeAnnotation(annotation);
            }            
            currentAnnotations.delete(uuid);
        }
    }
    console.log("Now have annotations: ", currentAnnotations.size);
}

export function updateOverlays(items = [], overlayType) {
    if (!map || !overlayType) return;
    let workingOverlays = currentOverlays[overlayType] ?? new Map();
    const itemIds = new Set();
    for (const x of items) {
        itemIds.add(x.uuid);
        if (!workingOverlays.has(x.uuid)) {
            const overlays = makeOverlays(x, overlayType);
            if (Array.isArray(overlays)) {
                map.addOverlays(overlays);
                workingOverlays.set(x.uuid, overlays);
            }
        }
    }
    // Remove overlays that are no longer needed
    for (const [uuid, overlays] of workingOverlays.entries()) {
        if (!itemIds.has(uuid)) {
            if (!Array.isArray(overlays)) {
                console.warn("Expected array of overlays for UUID:", uuid);
                continue;
            }
            let removedAnyOverlays = false;
            overlays.forEach(overlay => {
                if (!map.overlays.includes(overlay)) {
                    console.warn("Overlay not found on map, skipping removal:", uuid, overlay);
                } else {
                    map.removeOverlay(overlay);
                    removedAnyOverlays = true;
                }
            });
            if (removedAnyOverlays) {
                workingOverlays.delete(uuid);
            }
        }
    }
    console.log(`${overlayType}: Now have overlay groups: `, workingOverlays.size);
}

function makeAnnotationWithCallout(item) {
    const annotOptions = {
        title: item.name ?? item.wpn2_type,
        url: { 1: `data:image/png;base64,${sysData.icons[item.icon_name]}` },
        // ALFX_FUTURE: Pass icon size dynamically based on media class
        size: { width: 32, height: 32 },
        anchorOffset: AnnotOffset,
        data: { mappable: item },
        callout: summaryCalloutDelegate
    };
    const coord = coordinateOfMappable(item);
    const annotation = new mapkit.ImageAnnotation(coord, annotOptions);
    annotation.className = "map-annot-view";
    annotation.element.style.cursor = "pointer";
    return annotation;
}

function makeOverlays(item, overlayType) {
    switch (overlayType) {
        case 'Pipe':
            let pColor = pipeColor(item);
            const pipeCoords = coordinatesOfPipe(item);
            return [createPolylineOverlay(pipeCoords, pColor, 2)];

        case 'PipeConnection':
            const radius = map.region.span.latitudeDelta >= 0.0005 ? 0.5 : 0.125;
            const connectionCoords = connectionCoordinatesOfPipe(item);
            const pcOverlays = connectionCoords.map(c => Object.assign(new mapkit.CircleOverlay(c, radius)));
            pcOverlays.forEach(x => {x.style = pipeConnectionStyle});
            return pcOverlays;

        case 'Sensor':
            const coord = coordinateOfMappable(item);
            const overlayColor = statusColorMap[item.status ?? ""] || "gray";
            const circle = new mapkit.CircleOverlay(coord, 100.0);
            circle.style = heatMapStyles[overlayColor];
            return [circle];

        case 'SoundPath':
            const paths = Object.values(item?.['hydrant_paths'] ?? [])
            let lineWidth = 8;
            const pathOverlays = paths.map(entry => {
                const pathColor = entry?.['color'] ?? "#B2B2B2";    // SensorGray
                const coords = entry?.['coordinates'] ?? [];
                const mapCoords = coords.map(x => new mapkit.Coordinate(x[0], x[1]));
                const overlay = createPolylineOverlay(mapCoords, pathColor, lineWidth);
                lineWidth -= 2;
                return overlay;
            });
            return pathOverlays;
         
        default:
            break;
    }
}

export function searchDatabase(sText, dbData) {
    if (!sText) return;
    for (const entity of mappableEntitiesInSearchOrder()) {
        const items = dbData[entity.name] ?? [];
        const match = items.find(item => doesItemMatch(item, sText));
        if (match) return match;
    }
    return undefined;
}

export function panMapToItem(item) {
    const center = coordinateOfMappable(item);
    if (!map || !center || !item?.uuid) return;
    const span = new mapkit.CoordinateSpan(0.005, 0.01);
    const region = new mapkit.CoordinateRegion(center, span);
    map.setRegionAnimated(region);
}

export function refreshMap(dbData) {
    const mapRect = map.visibleMapRect;
    // 1. Map annotations for all mappables
    const annotatableCategories = allCategories().filter(cat => ["assets", "nhm", "utility"].includes(cat.name));
    const selectedCategories = annotatableCategories.filter(cat => mapPrefs.categories.includes(cat.name));
    const allItems = itemsForCategories(selectedCategories, dbData);
    const filteredItems = (mapPrefs.showServices === "on") ? allItems : allItems.filter(x => !isService(x));
    const displayItems = filteredItems.filter(x => willDisplayPointItem(x, mapRect));
    updateAnnotations(displayItems);
    // 2. Pipe overlays
    const allPipes = dbData.all?.['Pipe'] || [];
    const filteredPipes = mapPrefs.showServices === "on" ? allPipes : allPipes.filter(x => !isService(x));
    const displayPipes = filteredPipes.filter(x => mapPrefs.modeDrawPipe !== "hidden" && willDisplayPipeItem(x, mapRect));
    updateOverlays(displayPipes, 'Pipe');
    updateOverlays(displayPipes, 'PipeConnection');
    // 3. Color map overlay
    const heatMapItems = mapPrefs.categories.includes('alfx-heat-map') ? dbData.alfx_data['Sensor'] ?? [] : [];
    const displayHeatMaps = heatMapItems.filter(x => willDisplayPointItem(x, mapRect));
    updateOverlays(displayHeatMaps, 'Sensor');
}

export function showPathOverlays(paths) {
    let lineWidth = 8;
    paths.forEach(entry => {
        const pathColor = entry?.['color'] ?? "#B2B2B2";    // SensorGray
        const coords = entry?.['coordinates'] ?? [];
        const mapCoords = coords.map(x => new mapkit.Coordinate(x[0], x[1]));
        createPolylineOverlay(mapCoords, pathColor, lineWidth);
        lineWidth -= 2;
    });
}

function createPolylineOverlay(coords, color = "#B2B2B2", lineWidth = 2) {
    if (!Array.isArray(coords) || coords.length < 2 || !map) {
        return undefined;
    }
    const clampedWidth = Math.max(lineWidth, 1);
    const style = new mapkit.Style({
        strokeColor: color,
        lineWidth: clampedWidth
    });
    const polyline = new mapkit.PolylineOverlay(coords, { style });
    map.addOverlay(polyline);
    return polyline;
}
    
function willDisplayPointItem(item, mapRect) {
    // Z0: Always show: hydrants, ALFX sensors & sound locations
    // Z1: Show mains + distribution assets
    // Z2: Show everything
    const latDelta = map.region.span.latitudeDelta;
    const entity = item.entity ?? "";
    const type = item.wpn2_type ?? "";
    const subtype = item.sub_type ?? "";
    const infra = item.infrastructure ?? "";
    const isDeleted = item.delete_date && item.delete_date < Date.now() / 1000;
    if (latDelta >= 0.075) {
        return entity === "Utility";
    }
    if (latDelta >= 0.00625) {
        if (!["Utility", "FixedAsset", "SoundLocation", "Sensor"].includes(entity)) return false;
        if (!["Hydrant", "ALFX"].includes(type)) return false;
    } else if (latDelta >= 0.0025) {
        if (["Service", "Lateral", "Accessory"].includes(subtype)) return false;
    }
    return mapPrefs.infrastructures.includes(infra) && !isDeleted && isMappableVisible(item, mapRect);
}

function willDisplayPipeItem(item, mapRect) {
    // Z0: No pipes, Z1: Show mains pipes, Z2: Show everything
    const latDelta = map.region.span.latitudeDelta;
    const subtype = item.sub_type ?? "";
    const infra = item.infrastructure ?? "";
    const isDeleted = item.delete_date && item.delete_date < Date.now() / 1000;
    if (latDelta >= 0.025) return false;
    if (latDelta >= 0.00625 && ["Service", "Lateral", "Accessory"].includes(subtype)) return false;
    return mapPrefs.infrastructures.includes(infra) && !isDeleted && isPipeVisible(item, mapRect);
}

function isCoordVisible(coord, mapRect) {
    if (!coord) return false;
    const { x, y } = coord.toMapPoint();
    return x >= mapRect.minX() && x <= mapRect.maxX() &&
           y >= mapRect.minY() && y <= mapRect.maxY();
}

function isMappableVisible(item, mapRect) {
    const coord = coordinateOfMappable(item);
    return (isCoordVisible(coord, mapRect));
}

function isPipeVisible(item, mapRect) {
    const coords = coordinatesOfPipe(item);
    return (coords.some(coord => isCoordVisible(coord, mapRect)));
}

var summaryCalloutDelegate = {
    // Returns a div element.
    calloutContentForAnnotation: function (annotation) {
        var item = annotation.data.mappable;
        var aView = document.createElement("div");
        aView.style.width = "200px";
        aView.style.height = "64px";
        aView.style.borderRadius = "4px";
        aView.style.borderLeft = "6px solid " + allInfrastructures().find((x) => x.value === item.infrastructure)?.color ?? "#7F7F7F";
        var titleBox = aView.appendChild(document.createElement("div"));
        titleBox.className = "title-box"
        var title = titleBox.appendChild(document.createElement("h4"));
        title.className = "title-label" 
        title.textContent = item.name ?? item.wpn2_type;
        var line1 = titleBox.appendChild(document.createElement("p"));
        var line2 = titleBox.appendChild(document.createElement("p"));
        line1.className = "subtitle-label subtitle-line-1"
        line2.className = "subtitle-label subtitle-line-2"
        const twoLineSubTitle = subtitleLinesForItem(item);
        line1.textContent = twoLineSubTitle[0];
        line2.textContent = twoLineSubTitle[1];
        var link = aView.appendChild(document.createElement("a"));
        var imageInfo = document.createElement("img");
        imageInfo.src = "/images/iconInfo44@2x.png";
        imageInfo.style.width = "44px";
        imageInfo.style.height = "44px";
        link.appendChild(imageInfo);
        link.href = "#";
        link.className = "more-link-btn";
        link.onclick = function () {
            // Invokes a window function in the calling Svelte component
            reviewClicked(annotation.data.mappable);
        }
        return aView;
    }
};

function subtitleLinesForItem(item) {
    switch (item.entity) {
        case "FixedAsset":
        case "Utility":
        case "SoundLocation":
            return twoLineAddressOfMappable(item) ?? ["No address", ""];

        case "Pipe":
            const matText = item.material ?? "Unknown material";
            const diaText = (item.size != undefined) ? (item.size / 25.4).toFixed(2) + " in" : "";
            return [matText, diaText];

        case "Activity":
        case "WorkOrder":
        case "Issue":
            const workStatus = item.status ?? "";
            const lastUpdated = dateTextforTimeMS(item.last_updated);
            return [(item.device_sn ?? "Unknown") + ': ' + workStatus, lastUpdated];

        case "Sensor":
            // ALFX_TODO: Callout for a sensor should show colored dot for status
            const sensorStatus = item.status ?? "";
            const lastRead = dateTextforTimeMS(item.last_updated);
            return [(item.device_sn ?? "Unknown") + ': ' + sensorStatus, lastRead];

        default:
            break;
    }
    return ["", ""];
}

function pipeColor(item) {
    if (item?.entity !== "Pipe") return;
    const fallback = sysData.colors["Unknown"] ?? "#7F7F7F";
    if (mapPrefs.modeDrawPipe === 'infrastructure' || mapPrefs.modeDrawPipe === 'material') {
        return sysData.colors[item?.[mapPrefs.modeDrawPipe] ?? 'Unknown'] ?? fallback;
    }
    if (mapPrefs.modeDrawPipe === 'size') {
        const dia = (item?.size ?? 0) / 25.4;
        const match = allPipeSizes().find(x => dia <= x.limit);
        return sysData.colors[match?.value] ?? fallback;
    }
    return fallback;
}
